import React, { useState, useCallback, useLayoutEffect, useMemo } from "react";
import { Formik, Form } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";
import {
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import API from "../../utils/Axios.utils";
import FIelds from "../../uicomponents/FIelds";
import CustomSelect from "../../uicomponents/CustomSelect";
import TextArea from "../../uicomponents/TextArea";
import DateField from "../../utils/DateField";
import useAuth from "../../hooks/useAuth";
import { parse, format, isValid } from "date-fns";

const MAX_CHARS = 100;

const toDatetimeLocalValue = (backendValue) => {
  if (!backendValue) return "";
  if (typeof backendValue === "string" && backendValue.includes("T")) {
    const isoDate = new Date(backendValue);
    if (isValid(isoDate) && !isNaN(isoDate.getTime())) return format(isoDate, "yyyy-MM-dd'T'HH:mm");
  }
  const parsed = parse(backendValue, "yyyy-MM-dd hh:mm a", new Date());
  if (isValid(parsed)) return format(parsed, "yyyy-MM-dd'T'HH:mm");
  const fallback = new Date(backendValue);
  return isValid(fallback) && !isNaN(fallback.getTime()) ? format(fallback, "yyyy-MM-dd'T'HH:mm") : "";
};

const allStatus = [
  { id: 1, value: "Vendor_Submission", label: "Vendor Submission" },
  { id: 2, value: "Internal_Evaluation", label: "Internal Evaluation" },
  { id: 3, value: "Internal_Screening", label: "Internal Screening" },
  { id: 4, value: "Internal_Feedback", label: "Internal Feedback" },
  { id: 5, value: "Submitted_To_Client", label: "Submitted to Client" },
  { id: 6, value: "Client_Evaluation", label: "Client Evaluation" },
  { id: 14, value: "Awaiting_for_slots", label: "Awaiting for slots" },
  { id: 7, value: "L1_Round", label: "L1 Round" },
  { id: 8, value: "L2_Round", label: "L2 Round" },
  { id: 9, value: "L3_Round", label: "L3 Round" },
  { id: 18, value: "Selected_Onboarding", label: "Pending Offer" },
  { id: 15, value: "Onboarding_In_Process", label: "Onboarding in process" },
  { id: 10, value: "Onboarded", label: "Onboarded" },
  { id: 11, value: "Rejected", label: "Rejected", color: "#f44336" },
  { id: 12, value: "Interview_Cancelled", label: "Interview Cancelled", color: "#ff9800" },
  { id: 13, value: "Position_on_hold", label: "Position on Hold", color: "#2196f3" },
  { id: 16, value: "No_Updates", label: "No Updates" },
  { id: 17, value: "Close-No_Client_Response", label: "Close-No Client Response", color: "#f44336" },
];

const AdminStatusUpdateForm = ({
  setAlertContent,
  setAlertType,
  setAlert,
  item,
  jobCode,
  handleDialog,
  setUpdate,
  update,
}) => {
  // ✅ ALL HOOKS AT THE VERY TOP - NO EXCEPTIONS
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [interviewText, setInterviewText] = useState("");
  const [profileText, setProfileText] = useState("");

  // Initialize text states when item changes
  useLayoutEffect(() => {
    if (item) {
      setInterviewText(item.interview_feedback || "");
      setProfileText(item.profile_feedback || "");
    }
  }, [item?.interview_feedback, item?.profile_feedback]);

  // ✅ MOVED HANDLERS TO TOP LEVEL - NO HOOKS IN CALLBACKS
  const handleInterviewChange = useCallback((e, setFieldValue) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARS) {
      setInterviewText(newText);
      setFieldValue("interview_feedback", newText);
    }
  }, []);

  const handleProfileChange = useCallback((e, setFieldValue) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARS) {
      setProfileText(newText);
      setFieldValue("profile_feedback", newText);
    }
  }, []);

  // Add this helper function
  const getFeedbackFieldsForStatus = useCallback((status) => {
    const profileOnlyStages = [
      "Vendor_Submission",
      "Internal_Evaluation",
      "Internal_Screening",
      "Internal_Feedback",
      "Submitted_To_Client",
      "Client_Evaluation"
    ];

    const interviewOnlyStages = [
      "L1_Round",
      "L2_Round",
      "L3_Round"
    ];

    const bothFeedbackStages = [
      "Rejected",
      "Interview_Cancelled",
      "Position_on_hold",
      "Close-No_Client_Response"
    ];

    if (profileOnlyStages.includes(status)) {
      return 'profile';
    } else if (interviewOnlyStages.includes(status)) {
      return 'interview';
    } else if (bothFeedbackStages.includes(status)) {
      return 'both';
    }

    return null;
  }, []);

  // ✅ initialValues - back to single datetime field
  const initialValues = useMemo(() => ({
    job_code: jobCode || "",
    candidate_name: item?.candidate_name || "",
    status: item?.detailed_status || "",
    profile_feedback: item?.profile_feedback || "",
    interview_feedback: item?.interview_feedback || "",
    start_date: item?.start_date || "",
    end_date: item?.end_date || "",
    interview_datetime: toDatetimeLocalValue(item?.interview_datetime),
    interview_link: item?.interview_link || "",
  }), [item, jobCode]);

  // ✅ validationSchema
  const validationSchema = useMemo(() => Yup.object().shape({
    status: Yup.string().required("Status is required"),
    start_date: Yup.string().when("status", {
      is: "Onboarded",
      then: (schema) => schema.required("Start Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    end_date: Yup.string().when(["status", "start_date"], {
      is: (status, start_date) => status === "Onboarded" && start_date,
      then: (schema) => schema
        .required("End Date is required")
        .test("end-after-start", "End Date cannot be before Start Date", function (value) {
          if (!this.parent.start_date || !value) return true;
          return new Date(value) >= new Date(this.parent.start_date);
        }),
      otherwise: (schema) => schema.nullable(),
    }),
    // ✅ Single validation for interview_datetime
    interview_datetime: Yup.string().when("status", {
      is: (status) => ["L1_Round", "L2_Round", "L3_Round"].includes(status),
      then: (schema) => schema
        .required("Interview Date & Time is required")
        .test("future-date", "Interview Date & Time cannot be in the past", function (value) {
          if (!value) return true;
          const now = new Date();
          // Allow current minute
          now.setSeconds(0, 0);
          return new Date(value) >= now;
        }),
      otherwise: (schema) => schema.nullable(),
    }),
    interview_link: Yup.string().nullable().test(
      "valid-url",
      "Please enter a valid meeting link (e.g. https://meet.google.com/abc-defg-hij)",
      (value) => {
        if (!value || value.trim() === "") return true;
        try {
          const u = new URL(value.trim());
          if (u.protocol !== "http:" && u.protocol !== "https:") return false;
          // Must contain a dot in the hostname (e.g., meet.google.com, zoom.us) and at least 2 parts
          if (!u.hostname.includes(".") || u.hostname.startsWith(".") || u.hostname.endsWith(".")) return false;
          // Must contain a non-empty path or search param if meeting url
          return true;
        } catch {
          return false;
        }
      }
    ),
    profile_feedback: Yup.string().max(MAX_CHARS, `Profile feedback must be at most ${MAX_CHARS} characters`),
    interview_feedback: Yup.string().max(MAX_CHARS, `Interview feedback must be at most ${MAX_CHARS} characters`),
  }), []);

  // ✅ onSubmit - simplified back to single datetime
  const onSubmit = useCallback(async (values, { setSubmitting }) => {
    // const token = getToken();
    if (!token) {
      setAlertContent("Authentication token not found");
      setAlertType("error");
      setAlert(true);
      return;
    }

    const config = { headers: { Authorization: `Bearer ${token}` } };

    if (values.profile_feedback?.length > 5000 || values.interview_feedback?.length > 5000) {
      setAlertContent("Feedback cannot exceed 5000 characters.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    const feedbackType = getFeedbackFieldsForStatus(values.status);

    const data = {
      detailed_status: values.status,
      // Only send feedback if applicable to the status
      ...(feedbackType === 'profile' && { profile_feedback: values.profile_feedback || "" }),
      ...(feedbackType === 'interview' && { interview_feedback: values.interview_feedback || "" }),
      ...(feedbackType === 'both' && {
        profile_feedback: values.profile_feedback || "",
        interview_feedback: values.interview_feedback || ""
      }),
      ...(values.status === "Onboarded" && {
        start_date: values.start_date || null,
        end_date: values.end_date || null,
      }),
      ...([
        "L1_Round",
        "L2_Round",
        "L3_Round",
      ].includes(values.status) && {
        interview_datetime: values.interview_datetime || null,
        interview_link: values.interview_link || null,
      }),
    };

    try {
      await API.put(`/api/profile/supplier_candidate/job/${item.profile_id}`, data, config);
      queryClient.invalidateQueries(['jobs']);
      handleDialog();
      setUpdate(update + 1);
    } catch (error) {
      console.error("Update error:", error);
      const errorData = error?.response?.data;
      let errorMessage = "Update failed";

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (typeof errorData?.message === "string") {
        errorMessage = errorData.message;
      } else if (typeof errorData?.error === "string") {
        errorMessage = errorData.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setAlertContent(errorMessage);
      setAlertType("error");
      setAlert(true);
    } finally {
      setSubmitting(false);
    }

  }, [item?.profile_id, token, setAlertContent, setAlertType, setAlert, handleDialog, setUpdate, update]);

  // ✅ Early return AFTER all hooks
  if (!item) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Loading candidate data...
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({ values, touched, errors, setFieldValue, handleBlur, isSubmitting }) => {
        const feedbackType = getFeedbackFieldsForStatus(values.status);

        return (
          <Form noValidate>
            <Grid container spacing={2}>
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <span style={{ fontSize: "16px" }}>Job Code</span>
                <FIelds name="job_code" props={{ values, touched, errors, setFieldValue }} disabled="disabled" />
              </Grid>
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <span style={{ fontSize: "16px" }}>Candidate Name</span>
                <FIelds name="candidate_name" props={{ values, touched, errors, setFieldValue }} disabled="disabled" />
              </Grid>
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <span style={{ fontSize: "16px" }}>Status</span>
                <CustomSelect name="status" props={{ values, touched, errors, setFieldValue }} options={allStatus} />
              </Grid>
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12} />

              {values.status === "Onboarded" && (
                <>
                  <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "16px", marginBottom: "4px" }}>Start Date</span>
                      <DateField 
                        name="start_date" 
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue("start_date", val);
                          if (val) {
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                              d.setDate(d.getDate() + 90);
                              const endStr = d.toISOString().split("T")[0];
                              setFieldValue("end_date", endStr);
                            }
                          }
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "16px", marginBottom: "4px" }}>End Date</span>
                      <DateField name="end_date" />
                    </div>
                  </Grid>
                </>
              )}

              {/* ✅ SINGLE COMBINED DateTime + Link FIELD */}
              {["L1_Round", "L2_Round", "L3_Round"].includes(values.status) && (
                <>
                  <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "16px", marginBottom: "4px" }}>
                        Interview's Date & Time
                      </span>
                      <TextField
                        fullWidth
                        name="interview_datetime"
                        type="datetime-local"
                        value={values.interview_datetime || ""}
                        onChange={(e) => setFieldValue("interview_datetime", e.target.value)}
                        error={touched.interview_datetime && Boolean(errors.interview_datetime)}
                        helperText={touched.interview_datetime && errors.interview_datetime}
                        inputProps={{
                          step: 60,
                          min: (() => {
                            const now = new Date();
                            const year = now.getFullYear();
                            const month = String(now.getMonth() + 1).padStart(2, '0');
                            const day = String(now.getDate()).padStart(2, '0');
                            const hours = String(now.getHours()).padStart(2, '0');
                            const minutes = String(now.getMinutes()).padStart(2, '0');
                            return `${year}-${month}-${day}T${hours}:${minutes}`;
                          })()
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "16px", marginBottom: "4px" }}>Interview's Link</span>
                      <TextField
                        fullWidth
                        name="interview_link"
                        value={values.interview_link}
                        onChange={(e) => setFieldValue("interview_link", e.target.value)}
                        onBlur={handleBlur}
                        error={touched.interview_link && Boolean(errors.interview_link)}
                        helperText={touched.interview_link && errors.interview_link}
                        placeholder="Interview's Link"
                      />
                    </div>
                  </Grid>
                </>
              )}


              {/* Conditional Feedback Fields */}
              {feedbackType === 'profile' || feedbackType === 'both' ? (
                <Grid item xl={6} lg={6} md={6} sm={12} xs={12} sx={{ position: "relative" }}>
                  <span style={{ fontSize: "16px" }}>Profile Feedback</span>
                  <TextArea
                    name="profile_feedback"
                    props={{ values, touched, errors, setFieldValue }}
                    value={profileText}
                    multiline
                    fullWidth
                    minRows={6}
                    onChange={(e) => handleProfileChange(e, setFieldValue)}
                    error={touched.profile_feedback && Boolean(errors.profile_feedback)}
                    helperText={touched.profile_feedback && errors.profile_feedback}
                    inputProps={{ maxLength: MAX_CHARS }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 16,
                      color: (values.profile_feedback || "").length >= MAX_CHARS
                        ? "error.main"
                        : "text.secondary",
                    }}
                  >
                    {(values.profile_feedback || "").length}/{MAX_CHARS}
                  </Typography>
                </Grid>
              ) : null}

              {feedbackType === 'interview' || feedbackType === 'both' ? (
                <Grid item xl={6} lg={6} md={6} sm={12} xs={12} sx={{ position: "relative" }}>
                  <span style={{ fontSize: "16px" }}>Interview Feedback</span>
                  <TextArea
                    name="interview_feedback"
                    props={{ values, touched, errors, setFieldValue }}
                    multiline
                    fullWidth
                    minRows={6}
                    value={interviewText}
                    onChange={(e) => handleInterviewChange(e, setFieldValue)}
                    error={touched.interview_feedback && Boolean(errors.interview_feedback)}
                    helperText={touched.interview_feedback && errors.interview_feedback}
                    inputProps={{ maxLength: MAX_CHARS }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 16,
                      color: (values.interview_feedback || "").length >= MAX_CHARS
                        ? "error.main"
                        : "text.secondary",
                    }}
                  >
                    {(values.interview_feedback || "").length}/{MAX_CHARS}
                  </Typography>
                </Grid>
              ) : null}

              {/* Empty grid for layout balance when only one feedback is shown */}
              {(feedbackType === 'profile' || feedbackType === 'interview') && feedbackType !== 'both' ? (
                <Grid item xl={6} lg={6} md={6} sm={12} xs={12} />
              ) : null}

              <Grid item xl={12} lg={12} md={12} sm={12} xs={12} sx={{ marginTop: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    disabled={isSubmitting}
                    style={{
                      background: "#2777AA",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "16px",
                      fontWeight: "500",
                      letterSpacing: 1,
                    }}
                    type="submit"
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AdminStatusUpdateForm;
